import { useEffect, useState } from 'react';
import { Button, Card, Col, Container, Row } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import logo from '../../images/common/kofc.png';
import stMary from '../../images/common/st-mary-delaware.png';
import ContentService, { ContentBlock } from '../../services/ContentService';
import EventsService from '../../services/EventsService';

type Upcoming = {
  id: number;
  title: string;
  fullTitle?: string;
  start: { toFormat: (fmt: string) => string };
  href?: string;
};

const pillars = [
  { id: 'charity', title: 'Charity', text: 'Serving those in need with compassion and action.' },
  { id: 'unity', title: 'Unity', text: 'Stronger together in faith, parish, and community.' },
  { id: 'fraternity', title: 'Fraternity', text: 'Building brotherhood through friendship and support.' },
  { id: 'patriotism', title: 'Patriotism', text: 'Honoring our country and those who defend our freedoms.' },
];

function Home() {
  const [upcoming, setUpcoming] = useState<Upcoming[]>([]);
  const [slides, setSlides] = useState<ContentBlock[]>([]);

  useEffect(() => {
    const eventsService = new EventsService();
    eventsService.getUpcomingEvents(9).then((evs) => setUpcoming(evs || []));
    const contentService = new ContentService();
    contentService.getByKey('home_carousel').then(setSlides);
  }, []);

  const featured = slides.slice(0, 2).map((s) => ({
    id: String(s.id),
    title: s.title || 'Featured',
    body: s.body || '',
    imageUrl: s.imageUrl || logo,
    linkUrl: s.linkUrl || '/events',
    internal: !!(s.linkUrl && s.linkUrl.startsWith('/')),
  }));

  return (
    <div className="home-page">
      <section className="hero">
        <div className="hero-media" style={{ backgroundImage: `url(${stMary})` }} />
        <div className="hero-overlay" />
        <Container className="hero-content">
          <div className="hero-top">
            <h1>
              <span className="hero-title-line">Catholic men serving Delaware</span>
            </h1>
            <div className="hero-title-row">
              <img src={logo} alt="" className="hero-crest" />
              <p className="hero-kicker">KofC Council 1056<br />Delaware, Ohio</p>
            </div>
          </div>
          <p className="hero-lead">
            Guided by faith. United in fraternity. Serving our parish, community, and nation through
            charity and brotherhood.
          </p>
          <div className="hero-actions hero-actions-right">
            <Button
              href={`https://www.kofc.org/join/`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-gold-outline"
            >
              Become a Knight
            </Button>
          </div>
        </Container>
      </section>

      <section className="section">
        <Container>
          <Row className="g-4 home-split align-items-stretch">
            <Col lg={8}>
              <div className="section-head">
                <h2>Who we are</h2>
              </div>
              <Row className="g-3 align-items-center mb-3">
                <Col md={2} className="text-center d-none d-md-block">
                  <img src={logo} alt="Knights of Columbus" className="about-crest" />
                </Col>
                <Col md={10}>
                  <p className="lead-about mb-0">
                    Knights of Columbus Delaware, OH Council 1056 is a proud and faithful men's group
                    promoting Catholic principles within our community and parish for over 100 years. We
                    are Catholic men who lead, serve, protect, and defend - better husbands, fathers,
                    sons, neighbors, and role models who put charity and community first.
                  </p>
                </Col>
              </Row>
              <Row className="g-3" id="principles">
                {pillars.map((p) => (
                  <Col sm={6} key={p.id}>
                    <Link to={`/principles/${p.id}`} className="stretched-card-link">
                      <Card className="soft-card pillar-card h-100">
                        <Card.Body>
                          <div className="pillar-mark" />
                          <h3 className="h5">{p.title}</h3>
                          <p className="mb-0 text-muted">{p.text}</p>
                          <span className="pillar-more">Learn more</span>
                        </Card.Body>
                      </Card>
                    </Link>
                  </Col>
                ))}
              </Row>
            </Col>

            <Col lg={4}>
              <Card className="soft-card whats-on-box">
                <Card.Header className="whats-on-header d-flex justify-content-between align-items-center">
                  <span>What's on</span>
                  <Link to="/events" className="section-link">
                    Full calendar
                  </Link>
                </Card.Header>
                <Card.Body className="p-0">
                  {upcoming.length === 0 && (
                    <div className="p-3 text-muted">Loading upcoming events...</div>
                  )}
                  <ul className="whats-on-list">
                    {upcoming.map((e) => {
                      const title = e.fullTitle || e.title || '';
                      const dateLabel = e.start ? `${e.start.toFormat('LLL dd')} - ${e.start.toFormat('ha').toLowerCase()}` : '';
                      const row = (
                        <>
                          <span className="whats-on-date">{dateLabel}</span>
                          <span className="whats-on-title">{title}</span>
                        </>
                      );
                      return (
                        <li key={e.id}>
                          {e.href?.startsWith('/') ? (
                            <Link to={e.href} className="whats-on-item">
                              {row}
                            </Link>
                          ) : e.href ? (
                            <a href={e.href} className="whats-on-item">
                              {row}
                            </a>
                          ) : (
                            <Link to="/events" className="whats-on-item">
                              {row}
                            </Link>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {featured.length > 0 && (
        <section className="section section-muted">
          <Container>
            <div className="section-head">
              <h2>Featured</h2>
            </div>
            <Row className="g-3">
              {featured.map((f) => {
                const card = (
                  <Card className="featured-card h-100">
                    <Row className="g-0 h-100">
                      <Col md={5} className="featured-media-col">
                        <img src={f.imageUrl as string} alt="" className="featured-media" />
                      </Col>
                      <Col md={7}>
                        <Card.Body>
                          <div className="eyebrow">Fundraiser</div>
                          <h3 className="h4">{f.title}</h3>
                          {f.body && <p className="mb-3">{f.body}</p>}
                          <span className="text-gold">Get details â†’</span>
                        </Card.Body>
                      </Col>
                    </Row>
                  </Card>
                );
                return (
                  <Col lg={6} key={f.id}>
                    {f.internal ? (
                      <Link to={f.linkUrl} className="stretched-card-link">
                        {card}
                      </Link>
                    ) : (
                      <a href={f.linkUrl} className="stretched-card-link">
                        {card}
                      </a>
                    )}
                  </Col>
                );
              })}
            </Row>
          </Container>
        </section>
      )}
    </div>
  );
}

export default Home;
